<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import Message from 'primevue/message'
import ConfirmDialog from '@/components/ui/Dialog.vue'
import AdvancesCard from '@/components/ui/AdvancesCard.vue'
import Tag from 'primevue/tag'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { useAuthStore } from '@/stores/auth'
import { quoteApi, type Advisor, type PriceType, type Quote, type QuoteAdvance, type QuoteItem, type QuoteItemInput, type QuoteLine, type QuotePayload } from '@/services/quotes'
import { catalogApi, productApi, setApi, type CatalogSearchHit, type SetType } from '@/services/catalog'
import { customerApi, type CustomerSuggestion } from '@/services/customers'
import { usersApi } from '@/services/users'
import { formatCurrency } from '@/utils/format'

/**
 * Cotizador.
 *
 * Crea un borrador en el servidor al entrar y guarda sobre ese id. Pedirlo al
 * backend y no inventarlo en el cliente significa que existe un `quote_id` al
 * que auto-guardar desde el primer segundo: si no, un corte de luz antes del
 * primer guardado dejaría la cotización entera perdida.
 *
 * El total se calcula en pantalla **solo para mostrar**. El que se guarda es el
 * del servidor: si el cliente manipulase el total, el cotizador sería un campo
 * donde escribir cualquier cifra.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const allowed = computed(() => authStore.hasPermission('quotes.manage'))

/**
 * ¿Esto es una edición?
 *
 * Lo dice la ruta y no un parámetro: `/quotes/create` y `/quotes/:id/edit` llevan la
 * misma pantalla porque es la misma pantalla, y por qué se entra es cosa de la ruta.
 */
const editId = computed(() => {
  const bruto = String(route.params.id ?? '')
  const id = Number.parseInt(bruto, 10)

  return Number.isInteger(id) && id > 0 ? id : null
})
const isEdit = computed(() => editId.value !== null)

/**
 * Pestañas expandidas del acordeón.
 *
 * Con la estructura nueva de PrimeVue (`AccordionPanel` con su `value`) los
 * valores son los que se les pone, así que se usan nombres y no índices: `datos` y
 * `anticipos` dicen de qué panel se trata, y `0` y `1` obligaban a contar.
 *
 * Se enlaza con `v-model:value` porque el componente declara la prop `value` y
 * emite `update:value`. Con `v-model` a secas el valor llegaba como atributo
 * suelto —se veía escrito en el DOM y parecía puesto— y nadie lo leía.
 */
const expandedTabs = ref<string[]>([])

// En edición, el acordeón de datos debe empezar expandido.
// Se hace en onMounted (no en watch) porque el acordeón está dentro de
// v-if="allowed" y solo existe en el DOM cuando allowed ya es true.
onMounted(() => {
  // El acordeón de datos empieza abierto en las dos operaciones: crear y editar.
  // Antes solo se abría al editar, y al crear había que pulsarlo para ver los
  // campos que se acaban de llenar, que era un paso de más cada vez.
  expandedTabs.value = ['datos']
})

/**
 * A dónde se vuelve desde aquí, con los filtros que se trajeron.
 *
 * Al editor se llega desde el listado, que lleva sus filtros en la URL. Si se vuelve sin
 * ellos, el listado los pierde: se filtra, se abre una cotización, se edita, se vuelve, y
 * el filtro ha desaparecido.
 */
const { destino: destinoListado, volver: volverAlListado } = useVolverAlListado('quotes.index')

/** Líneas de atención disponibles (para el desplegable en edición). */
const attentionLines = ref<string[]>([])

async function loadAttentionLines(): Promise<void> {
  try {
    console.log('[loadAttentionLines] Starting...')
    const response = await usersApi.getAttentionLines()
    console.log('[loadAttentionLines] Response:', response)
    // El backend devuelve {data: [...]}, axios lo pone en response.data
    // así que el array está en response.data.data
    const lineas = response.data?.data
    console.log('[loadAttentionLines] Lineas:', lineas)
    attentionLines.value = Array.isArray(lineas) ? lineas : []
    console.log('[loadAttentionLines] attentionLines.value:', attentionLines.value)
  } catch (e) {
    console.error('[loadAttentionLines] Error:', e)
    attentionLines.value = []
  }
}

function volverALista(): void {
  // Siempre volver al listado, nunca a la ficha.
  // El botón "Atrás" en edición debe llevar a la lista con los filtros preservados,
  // no a la ficha de la cotización (eso requeriría dos clics para volver a la lista).
  volverAlListado()
}

/**
 * El título de la pantalla.
 *
 * Sin el nombre del cliente. Se puso porque una lista de correcciones es fácil de
 * mezclar, pero salió un caso que lo delató: el nombre de un borrador vacío es el
 * texto de relleno «Borrador», así que la pantalla de edición de un borrador decía
 * «Editar cotización de Borrador». Un título que dice «Borrador» al lado de una
 * cotización que ya existe no aclara nada, y el nombre del cliente ya está en el campo
 * de abajo, en el sitio donde se escribe.
 */
const titulo = computed(() => (isEdit.value ? 'Editar cotización' : 'Nueva cotización'))

const quoteId = ref<number | null>(null)

/**
 * La cotización que se está editando, si es que se está editando.
 *
 * Solo hace falta para una cosa: saber si lleva factura emitida, porque entonces hay
 * que avisar antes de guardar.
 */
const editada = ref<Quote | null>(null)

/**
 * Los anticipos de la cotización que se está editando.
 *
 * Van aquí y no dentro del formulario porque no son parte de lo que se guarda con la
 * cotización: un anticipo es dinero que ya entró, se registra por su cuenta y puede
 * repetirse. La tarjeta es la misma que se ve en la ficha —va en su propio componente
 * por eso—, y aquí lo único que se hace es guardar lo que devuelve para no tener dos
 * copias que digan cosas distintas.
 */
/**
 * Si la cotización lleva una factura ya emitida.
 *
 * Es la excepción al autoguardado, y va antes que `isEdit` a propósito: se rellena
 * solo al editar, así que decir «si es una edición» y decir «si lleva factura» vienen
 * a ser lo mismo, pero lo que decide la regla es la factura.
 */
const estaFacturada = computed(() => editada.value?.is_invoiced === true)

/**
 * Si lo que se está escribiendo se guarda sin que nadie pulse nada.
 *
 * Lo que se pinta en pantalla es esto y no «si es el alta o la edición», porque es lo
 * que el usuario necesita saber: si lo que escribió se conserva solo o hay que pulsar
 * el botón.
 */
const seGuardaSolo = computed(() => !estaFacturada.value)

const advances = ref<QuoteAdvance[]>([])

/** Cuánto se ha pagado por adelantado. Lo recalcula el servidor. */
const advancesTotal = ref('0.00')

/** Lo que queda por cobrar. Sale del servidor y aquí no se vuelve a calcular. */
const balance = ref('0.00')

/** Recoge lo que cambia en la tarjeta de anticipos. */
function cambiaAnticipos(totals: { advances_total: string; balance: string; advances: QuoteAdvance[] }): void {
  advances.value = totals.advances
  advancesTotal.value = totals.advances_total
  balance.value = totals.balance
}

const saving = ref(false)
const loading = ref(true)
const generalError = ref<string | null>(null)
const serverErrors = ref<Record<string, string[]>>({})

const customerName = ref('')
/**
 * El documento del cliente, su NIT.
 *
 * Se llama `customerDocument` y no `document` a propósito: `document` es el DOM de la
 * página, y un `ref` con ese nombre lo tapa dentro del componente. Con el nombre corto,
 * `document.querySelector(...)` compila contra el ref y TypeScript lo señala —que es como
 * se detectó—, pero el día que alguien escriba algo más parecido el error se vuelve
 * silencioso.
 */
const customerDocument = ref('')
const customerEmail = ref('')
const customerPhone = ref('')
const customerCity = ref('')
const customerSource = ref('')
const customerAttentionLine = ref('')

/**
 * Opciones del desplegable de línea de atención.
 *
 * Son dos fuentes y hay que juntarlas: las líneas que tienen los usuarios ahora
 * mismo, y la que ya tiene esta cotización.
 *
 * La segunda hace falta porque una línea puede quedarse sin usuarios —porque se
 * les cambió o porque se desactivaron— y entonces el `Select` no la encuentra
 * entre sus opciones y pinta su texto de relleno. El campo se ve vacío cuando en
 * realidad tiene un valor dentro: quien lo mira no tiene forma de saber que está
 * puesto, y da por hecho que hay que elegirlo y se le puede cambiar sin querer.
 *
 * Se quitan las repetidas porque dos usuarios con la misma línea son la misma línea:
 * en el desplegable tiene que aparecer una sola vez. Si no, se lee como que hay
 * dos y no se sabe cuál elegir.
 *
 * «Sin línea» está a propósito: es la forma de quitarle la línea a una
 * cotización. Sin esa opción, un `Select` del que no se puede elegir el vacío
 * deja el valor puesto para siempre.
 */
const attentionLineOptions = computed(() => {
  const lineas = [...attentionLines.value, customerAttentionLine.value.trim()]

  return [...new Set(lineas.filter((linea) => linea.trim() !== ''))]
    .sort((a, b) => a.localeCompare(b, 'es'))
    .map((linea) => ({ label: linea, value: linea }))
})

/** Opciones completas para el Select, incluyendo "Sin línea". */
const attentionLineSelectOptions = ref<Array<{ label: string; value: string }>>([])

function actualizarAttentionLineSelectOptions(): void {
  const result = [
    { label: 'Sin línea', value: '' },
    ...attentionLineOptions.value,
  ]
  attentionLineSelectOptions.value = result
}

watch(attentionLineOptions, actualizarAttentionLineSelectOptions, { immediate: true })

/** Devuelve las opciones para el Select, forzando re-evaluación en cada render. */
// getAttentionLineSelectOptionsForSelect eliminado - usando ref reactiva directamente
const customerId = ref<number | null>(null)
const priceType = ref<PriceType>('retail')
const items = ref<QuoteLine[]>([])

/** Claves de las líneas con el acordeón abierto. */
const expandedKeys = ref<string[]>([])

/**
 * Asesor de la cotización.
 *
 * El backend lo aceptaba y lo validaba desde antes, y `normalizeHeader()` le ponía
 * el autor cuando no venía. Lo que no existía era la forma de **cambiarlo**: un
 * comercial que prepara una cita para un compañero no tenía dónde poner su nombre,
 * y el campo tampoco se veía en el listado ni en la ficha.
 *
 * Empieza en el usuario autenticado y no en el primero de la lista: una cotización
 * creada por un comercial es suya, y poner de entrada el asesor con el id más bajo
 * sería atribuirla a otro sin querer.
 */
const advisors = ref<Advisor[]>([])
const advisorId = ref<number | null>(null)

// ── Autoguardado ─────────────────────────────────────────────────────────

/**
 * Guardado automático de lo que se está escribiendo, **también al editar**.
 *
 * El endpoint existía en el servidor y el método estaba escrito en el cliente desde el
 * principio, y **nada lo llamaba**. Un corte de luz a media cotización la perdía
 * entera, que es justo lo que el cotizador crea al entrar: pide el borrador al backend
 * para que exista un `quote_id` al que guardar desde el primer segundo, y luego no
 * guardaba.
 *
 * Al principio el autoguardado era solo del borrador, porque una cotización ya
 * guardada no debía cambiar sola mientras el comercial la estaba leyendo. Se cambió
 * por lo contrario: en la edición se corrige a menudo **desde el teléfono**, en el
 * sitio, y perder la corrección entera porque se cerró la pestaña es peor que una
 * cotización que se guarda un segundo antes de lo que el comercial cree. El
 * autoguardado no cambia el estado de la cotización —eso solo lo hace el botón de
 * guardar— así que no saca nada del sitio donde estaba: la corrección se conserva y
 * la cotización sigue siendo la misma.
 *
 * Va con retardo y no en cada tecla. Sin retardo, escribir «Comercial Andina» serían
 * dieciséis peticiones, y las respuestas pueden llegar desordenadas: la que saliera
 * última se guardaría y podría ser una de hace un segundo, escribiendo el trabajo por
 * detrás de lo último tecleado. El retardo es lo que hace que eso no pueda pasar, así
 * que no se quita: guardar «de inmediato» significa sin que nadie pulse nada, no sin
 * esperar una fracción de segundo.
 *
 * Un fallo aquí no interrumpe: se avisa y se sigue escribiendo. Bloquear el formulario
 * por un fallo de red sería peor que perder el último cambio, porque el trabajo se
 * seguiría teniendo delante de los ojos.
 */

/** Cuánto se espera sin escribir antes de guardar. */
const AUTO_SAVE_DELAY_MS = 700

const autoSaving = ref(false)
const autoSaveError = ref<string | null>(null)

/**
 * Un autoguardado en marcha.
 *
 * Solo puede haber uno. Dos a la vez llegarían al servidor en orden desconocido, y el
 * que contestara el último escribiría encima —o por debajo— del otro.
 */
let autoSaveEnCurso = false

/**
 * Quedó un cambio sin guardar mientras el anterior estaba en marcha.
 *
 * Antes esto no existía y el cambio se **descartaba**: `if (autoSavePending) return`.
 * Con el autoguardado solo en el borrador casi no se notaba, porque entre autoguardados
 * había un segundo entero y rara vez se escribía justo en ese instante. Con el
 * autoguardado en la edición es el caso normal —el comercial escribe sin parar—, y el
 * efecto era que el último cambio se perdía salvo que alguien volviera a escribir
 * después.
 */
let autoSavePendiente = false

let autoSaveTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Lo último que se ha guardado, en forma de firma.
 *
 * Es el cuerpo del guardado convertido en texto, y sirve para dos cosas: no mandar
 * otra vez lo que ya está guardado, y saber si el relleno inicial del formulario es un
 * cambio o no. Entrar a editar una cotización y no escribir nada **no** es cambiar
 * nada: el autoguardado compara contra esto y se queda quieto.
 *
 * Guarda la firma de lo que **se mandó**, no la que hay en pantalla al terminar. Si se
 * escribió mientras guardaba, lo escrito sigue sin guardar y tiene que volver a salir:
 * marcar como guardado lo que no lo está es justo la forma de perderlo en silencio.
 */
let ultimaFirmaGuardada: string | null = null

/** La firma de lo que hay ahora mismo en pantalla. */
function firmaActual(): string {
  return JSON.stringify(payloadActual())
}

/**
 * Las líneas sin la clave local, en cascada.
 *
 * La clave es del cliente —sirve para que el acordeón abierto no se pegue al set de al
 * lado cuando cambia un índice— y no viaja: el backend no la conoce y mandarla sería
 * mandar un campo que no está en su contrato. Los hijos son `QuoteItemInput` de
 * verdad, sin `key`, así que la función solo tiene que quitar la de la raíz.
 */
function itemParaEnviar(item: QuoteItemInput): QuoteItemInput {
  const { key: _clave, ...resto } = item as QuoteItemInput & { key?: string }

  return {
    ...resto,
    children: resto.children?.map((hijo) => ({
      ...hijo,
      children: hijo.children?.map((nieto) => itemParaEnviar(nieto)),
    })),
  }
}

/**
 * Los orígenes que se pueden elegir.
 *
 * Los mismos cuatro que el directorio usa, y no un texto libre: el origen se segmenta y un
 * «recomendado de Juan» no es un segmento. Un desplegable con «Sin origen» de primera
 * opción, porque hay clientes de los que no se sabe de dónde vinieron.
 */
const customerSourceOptions: Array<{ label: string; value: string }> = [
  { label: 'Sin origen', value: '' },
  { label: 'Web', value: 'web' },
  { label: 'Referido', value: 'referido' },
  { label: 'Redes sociales', value: 'redes' },
  { label: 'Visita comercial', value: 'visita' },
]

function payloadActual(): QuotePayload {
  const base: QuotePayload = {
    customer_id: customerId.value,
    customer_name: customerName.value.trim(),
    document: customerDocument.value.trim() === '' ? null : customerDocument.value.trim(),
    customer_email: customerEmail.value.trim() === '' ? null : customerEmail.value.trim(),
    customer_phone: customerPhone.value.trim() === '' ? null : customerPhone.value.trim(),
    customer_city: customerCity.value.trim() === '' ? null : customerCity.value.trim(),
    customer_source: customerSource.value === '' ? null : customerSource.value,
    price_type: priceType.value,
    user_id: advisorId.value,
    items: items.value.map((item) => itemParaEnviar(item)),
  }

  // En creación, NO enviamos attention_line: el backend la autocompleta desde el usuario.
  // En edición, enviamos el valor seleccionado en el desplegable.
  if (isEdit.value) {
    base.attention_line = customerAttentionLine.value.trim() === '' ? null : customerAttentionLine.value.trim()
  }

  return base
}

/**
 * Si lo que se está escribiendo puede autoguardarse ahora mismo.
 *
 * La única excepción es la **factura emitida**: una factura no se deshace y su número
 * apunta a lo que se le cobró. Autoguardar sobre ella reescribiría por detrás el
 * documento que ya se facturó, sin que nadie lo pidiera y sin aviso, que es
 * exactamente lo que el aviso de guardar existe para evitar. En una cotización
 * facturada se sigue escribiendo igual, se ven los cambios al momento, y se guardan
 * con el botón de guardar, que pregunta antes.
 */
function puedeAutoGuardar(): boolean {
  return !estaFacturada.value
}

async function autoSave(): Promise<void> {
  // Sin borrador no se puede autoguardar. Pasa solo si se escribe antes de que el
  // `createDraft` termine, que es la carrera de los primeros milisegundos.
  if (quoteId.value === null) return
  if (!puedeAutoGuardar()) return

  // Lo que ya está guardado no se vuelve a mandar. Cada autoguardado borra los
  // artículos y los vuelve a crear, así que mandar lo mismo una y otra vez es trabajo
  // de base de datos para no cambiar nada.
  const firma = firmaActual()
  if (firma === ultimaFirmaGuardada) return

  if (autoSaveEnCurso) {
    // No se descarta: se anota que hay algo más y se vuelve a guardar al terminar el
    // que está en marcha.
    autoSavePendiente = true

    return
  }

  autoSaveEnCurso = true
  autoSaving.value = true
  autoSaveError.value = null

  try {
    const { data } = await quoteApi.autoSave({ ...payloadActual(), quote_id: quoteId.value })

    // El autoguardado es un *upsert*: si el `quote_id` no existe ya, crea la
    // cotización. Guardar el id que conteste es lo que hace que lo que se escribió
    // después siga teniendo dónde guardarse.
    quoteId.value = data.data.quote_id
    ultimaFirmaGuardada = firma
  } catch {
    autoSaveError.value =
      'No se pudo autoguardar. Tu trabajo sigue aquí; vuelve a intentarlo al guardar.'
  } finally {
    autoSaveEnCurso = false
    autoSaving.value = false

    if (autoSavePendiente) {
      autoSavePendiente = false
      programarAutoSave()
    }
  }
}

function programarAutoSave(): void {
  // El temporizador se cancela siempre: lo que se estaba esperando ya no importa si
  // hay algo más nuevo esperando detrás.
  if (autoSaveTimer !== null) {
    clearTimeout(autoSaveTimer)
    autoSaveTimer = null
  }

  if (!puedeAutoGuardar()) return

  autoSaveTimer = setTimeout(() => {
    void autoSave()
  }, AUTO_SAVE_DELAY_MS)
}

/**
 * Cualquier cambio programa un autoguardado, y se vigila **la firma del cuerpo que se
 * manda**, no una lista de campos.
 *
 * Vigilar la firma tiene tres ventajas sobre la lista de campos que había:
 *
 * - **Se ve cualquier cambio.** Los campos de un artículo se escriben con
 *   `v-model="item.price"`, que muta el objeto que ya está dentro del array. Un
 *   `watch` normal no ve esa mutación, así que cambiar el **precio o la cantidad de
 *   una línea que ya estaba escrita** no programaba nada: el autoguardado no se
 *   disparaba y el cambio se perdía. Lo mismo pasaba con los componentes de un set, y
 *   con la línea de atención, que no estaba en la lista.
 * - **No hay lista que mantener.** Añadir un campo al cotizador y se olvidaba de añadirlo
 *   a la lista de vigilados; esa lista era un segundo sitio donde se podía olvidar
 *   algo.
 * - **No guarda más de lo que guarda.** Lo único que dispara el guardado es lo que
 *   entra en el cuerpo. El acordeón que se abre, el texto de un buscador o la clave
 *   local de una línea no se guardan porque no forman parte de lo guardado, y antes
 *   con `deep` habría que haberlo tenido en cuenta aparte.
 *
 * Compara contra la firma guardada, de modo que rellenar el formulario al abrir una
 * cotización no cuenta como cambio: lo que hay en pantalla ya está en el servidor.
 */
watch(firmaActual, (firma) => {
  if (firma === ultimaFirmaGuardada) return

  programarAutoSave()
})

/**
 * Al salir de la pantalla se guarda lo que haya pendiente, sin esperar el retardo.
 *
 * Es el caso que más se pierde: el comercial cierra la pestaña —o vuelve al listado—
 * y lo último que escribió se queda sin guardar. Y es el caso más fácil de resolver,
 * porque en ese momento ya no hay nada con lo que estorbar.
 *
 * Si lo último ya está guardado no se manda nada: `autoSave()` compara con la firma y
 * se queda quieto, así que ir al listado después de pulsar «Guardar cambios» no
 * genera una segunda escritura idéntica.
 */
onBeforeUnmount(() => {
  if (autoSaveTimer === null) return

  clearTimeout(autoSaveTimer)
  autoSaveTimer = null
  void autoSave()
})

/**
 * Monta la cotización que se va a editar.
 *
 * Devuelve las líneas con clave local nueva: la del servidor no existe en el cliente,
 * y sin ella el acordeón abierto se pegaría al set de al lado cuando cambia un índice.
 */
function lineasDesde(items: QuoteItem[]): QuoteLine[] {
  const lineas = items.map((item) => {
    const linea = nuevaLinea({
      set_id: item.set_id,
      product_id: item.product_id,
      custom_name: item.product_id === null && item.set_id === null ? item.name : null,
      reference: item.reference,
      name: item.name,
      price: Number.parseFloat(item.price),
      quantity: Number.parseFloat(item.quantity),
      children: item.children.map((hijo) => ({
        product_id: hijo.product_id,
        set_id: hijo.set_id,
        custom_name: hijo.product_id === null && hijo.set_id === null ? hijo.name : null,
        reference: hijo.reference,
        name: hijo.name,
        price: Number.parseFloat(hijo.price),
        quantity: Number.parseFloat(hijo.quantity),
      })),
    })

    // Las líneas cargadas desde el backend solo traen el precio guardado, sin los
    // dos precios del catálogo. Se van a buscar en segundo plano para que cambiar
    // el tipo de facturación también les actualice el precio, igual que a las
    // añadidas nuevas.
    void poblarPreciosDesdeCatalogo(linea)

    return linea
  })

  return lineas
}

/** Rellena el formulario con una cotización ya guardada. */
function rellenarCon(quote: Quote): void {
  customerId.value = quote.customer_id
  customerName.value = quote.customer_name
  customerDocument.value = quote.document ?? ''
  customerEmail.value = quote.customer_email ?? ''
  customerPhone.value = quote.customer_phone ?? ''
  customerCity.value = quote.customer_city ?? ''
  customerSource.value = quote.customer_source ?? ''
  customerAttentionLine.value = quote.attention_line ?? ''
  priceType.value = quote.price_type
  advisorId.value = quote.user_id
  items.value = lineasDesde(quote.items)

  // Los anticipos vienen con la cotización de la ficha. En el listado no vienen, y por
  // eso aquí se usan `?? []`: si la respuesta llega sin ellos, la tarjeta se enseña
  // vacía en vez de romperse.
  advances.value = quote.advances ?? []
  advancesTotal.value = quote.advances_total
  balance.value = quote.balance

  // Los sets se cargan **cerrados**. Al añadirlos se abren porque se van a escribir sus
  // componentes; al abrirlos para corregir algo, lo que se quiere es ver la cotización
  // entera de una pasada, y con diez sets abiertos debajo no se ve nada.
  expandedKeys.value = []
}

// ── Buscadores ────────────────────────────────────────────────────────────

/**
 * Dos buscadores en paralelo, no pestañas.
 *
 * Un pedido lleva piezas sueltas y conjuntos a la vez, así que alternar entre dos
 * modos para cada línea era un paso por línea que no purchases nada. Cada buscador
 * tiene su término y sus resultados, y escribir en uno no toca el otro.
 *
 * El de sets lleva además el filtro **Nacional / Premium**, que faltaba en el cotizador
 * aunque la tabla de catálogo ya lo tuviera.
 */
const productTerm = ref('')
const productHits = ref<CatalogSearchHit[]>([])
const productSearching = ref(false)

const setTerm = ref('')
const setType = ref<SetType | ''>('')
const setHits = ref<CatalogSearchHit[]>([])
const setSearching = ref(false)

const customerSuggestions = ref<CustomerSuggestion[]>([])
const searching = ref(false)

/** Un término de menos de dos letras no busca: devolvería el catálogo entero. */
const MIN_TERM_LENGTH = 2

const customerSuggestionsBuscables = computed(() => customerSuggestions.value)

/**
 * El buscador de cada set abierto.
 *
 * Va por la clave local de la línea y no por su índice: el índice cambia al quitar un
 * artículo, y con el índice el buscador abierto se quedaría escribiendo en el set de al
 * lado.
 */
const componentTerm = ref<Record<string, string>>({})
const componentHits = ref<Record<string, CatalogSearchHit[]>>({})
const componentSearching = ref<Record<string, boolean>>({})

/**
 * Tipo de facturación, con las palabras del comercial.
 *
 * Antes se llamaba «tipo de precio» con «minorista» y «mayorista», que son los
 * nombres de las columnas de la tabla de productos. El rótulo hablaba del
 * almacenamiento y no de lo que se está decidiendo: minorista es *para quién se
 * vende*, y el campo decide de qué columna se lee el precio de cada artículo.
 *
 * Sin el asterisco: es obligatorio, pero el desplegable no se puede dejar vacío —
 * siempre hay un valor— y un asterisco sobre algo que no se puede dejar en blanco
 * solo ruido.
 */
const priceTypeOptions = [
  { label: 'Venta al detal', value: 'retail' as const },
  { label: 'Venta al mayoreo', value: 'wholesale' as const },
]

/** Asesor: el propio usuario entre los activos. */
const advisorOptions = computed(() =>
  advisors.value.map((advisor) => ({ label: advisor.name, value: advisor.id })),
)

/** Solo los ítems padre suman al total: los hijos son el desglose de su set. */
const total = computed(() =>
  items.value.reduce((sum, item) => sum + item.price * item.quantity, 0),
)

const isEmpty = computed(() => items.value.length === 0)

/** Clave local de una línea. No es el índice: el índice cambia al quitar un artículo. */
let lineCounter = 0

function nuevaLinea(datos: Omit<QuoteItemInput, 'key'>): QuoteLine {
  lineCounter += 1

  return { ...datos, key: `l${lineCounter}` }
}

/**
 * Lleva la vista hasta la línea recién añadida.
 *
 * En una cotización larga, lo que se acaba de añadir cae al final de la lista y el
 * comercial no ve si ha salido bien: o hay que bajar a comprobarlo, o se añade a ciegas.
 * Solo se desplaza si el usuario está añadiendo por arriba, que es justo cuando acaba de
 * escribir en el buscador del principio.
 */
async function desplazarA(clave: string): Promise<void> {
  await nextTick()

  const nodo = document.querySelector(`[data-linea="${clave}"]`)
  if (nodo === null) return

  // `nearest` y no `start`: si la línea ya cabe en pantalla, saltarse el scroll es mejor
  // que mover la vista de sitio.
  //
  // Se comprueba que exista: `scrollIntoView` no está en el DOM de prueba, así que
  // llamarla a ciegas convierte un simple desplazamiento —que es cosmético— en una
  // excepción sin manejar que ensucia la puerta de pruebas con errores que no son
  // fallos del producto. En un navegador siempre existe, así que el caso real de
  // este `if` no llega nunca.
  if (typeof nodo.scrollIntoView === 'function') {
    nodo.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
}

function priceFor(item: CatalogSearchHit): number {
  return Number.parseFloat(priceType.value === 'wholesale' ? item.wholesale_price : item.retail_price)
}

/**
 * Cambiar el tipo de facturación reprecia lo que vino del catálogo.
 *
 * Solo se tocan las líneas que guardan los dos precios: los artículos
 * personalizados (sin catálogo) conservan el precio que el usuario escribió, y
 * las cargadas desde una cotización guardada sin precios no se tocan hasta que
 * se carguen sus precios del catálogo.
 *
 * Se hace en un watcher y no a mano porque el cambio puede venir del select, del
 * teclado o del autoguardado: un solo sitio que reprecia es lo que evita que
 * una vía olvide actualizar y quede el precio del otro tipo.
 */
watch(priceType, (nuevoTipo) => {
  const reprecio = (linea: QuoteLine): void => {
    if (linea.retail_price === undefined || linea.wholesale_price === undefined) return
    linea.price = nuevoTipo === 'wholesale' ? linea.wholesale_price : linea.retail_price

    for (const hijo of linea.children ?? []) {
      if (hijo.retail_price === undefined || hijo.wholesale_price === undefined) continue
      hijo.price = nuevoTipo === 'wholesale' ? hijo.wholesale_price : hijo.retail_price
    }
  }

  items.value.forEach(reprecio)
})

/**
 * Busca en el catálogo los precios de una línea cargada desde el backend.
 *
 * Al editar una cotización guardada, las líneas solo traen el precio que se
 * guardó: los dos precios del catálogo no viajan. Esta función los busca para
 * que cambiar el tipo de facturación también les actualice el precio, igual
 * que a las añadidas nuevas. Si el catálogo no responde o el artículo ya no
 * existe, se queda con el precio guardado: peor que actualizarlo, pero mejor
 * que borrarlo.
 */
async function poblarPreciosDesdeCatalogo(linea: QuoteLine): Promise<void> {
  try {
    if (linea.set_id !== null && linea.set_id !== undefined) {
      const { data } = await setApi.get(linea.set_id)
      linea.retail_price = Number.parseFloat(data.data.retail_price)
      linea.wholesale_price = Number.parseFloat(data.data.wholesale_price)
    } else if (linea.product_id !== null && linea.product_id !== undefined) {
      const { data } = await productApi.get(linea.product_id)
      linea.retail_price = Number.parseFloat(data.data.retail_price)
      linea.wholesale_price = Number.parseFloat(data.data.wholesale_price)
    }
  } catch {
    // Si no se pudieron cargar, se deja el precio guardado y el watcher no la
    // toca. Mejor un precio congelado que un error en mitad de la edición.
  }
}

/**
 * Añade un producto suelto.
 *
 * Entra con su precio de catálogo según el tipo de facturación de la cotización, que es
 * de qué columna se lee.
 */
function addProduct(found: CatalogSearchHit): void {
  // **Al principio de la lista**, no al final. Se añade desde el buscador, que está
  // arriba del todo: la línea nueva tiene que salir justo debajo, donde ya está la
  // vista, en vez de al final de treinta artículos donde hay que bajar a buscarla.
  //
  // Y en una corrección, que es donde más se nota: vas a cambiar tres cosas de una
  // cotización de cuarenta y las tres tienen que salir donde estás mirando.
  // Al añadirlo, el precio es el del catálogo según el tipo activo. Se guardan
  // los dos precios para poder cambiar de tipo sin recargar el catálogo.
  const linea = nuevaLinea({
    product_id: found.id,
    reference: found.reference,
    name: found.name,
    price: priceFor(found),
    quantity: 1,
    retail_price: Number.parseFloat(found.retail_price),
    wholesale_price: Number.parseFloat(found.wholesale_price),
  })

  items.value = [linea, ...items.value]

  productTerm.value = ''
  productHits.value = []
  void desplazarA(linea.key)
}

/**
 * Añade un set.
 *
 * Entra con **sus propios datos** de catálogo —referencia, nombre y precio—, que es lo
 * que el catálogo sabe. Y **sin componentes**: el catálogo no sabe qué lleva cada set,
 * porque no hay tabla de composición, así que quien los conoce es el comercial y los
 * escribe con el buscador que lleva dentro de la fila.
 *
 * Antes entraba con un hijo de muestra llamado «Componente del set» a precio 0, y el
 * comentario decía que los de verdad se añadían desde el detalle. Pero el detalle
 * **solo los mostraba**: no había ninguna parte de la aplicación donde corregirlos.
 */
function addSet(found: CatalogSearchHit): void {
  // Al añadirlo, el precio es el del catálogo según el tipo activo. Se guardan
  // los dos precios para poder cambiar de tipo sin recargar el catálogo.
  const linea = nuevaLinea({
    set_id: found.id,
    reference: found.reference,
    name: found.name,
    price: priceFor(found),
    quantity: 1,
    children: [],
    retail_price: Number.parseFloat(found.retail_price),
    wholesale_price: Number.parseFloat(found.wholesale_price),
  })

  items.value = [linea, ...items.value]

  // El set entra **abierto**, porque se añade para escribir lo que lleva dentro. Si
  // llegara cerrado se vería igual que un producto —con su nombre y su precio— y el
  // comercial tendría que adivinar que hay que pulsarlo para poder meterle los
  // componentes.
  expandedKeys.value = [...expandedKeys.value, linea.key]

  setTerm.value = ''
  setHits.value = []
  desplazarA(linea.key)
}

/** Añade un producto como componente del set de la línea `clave`. */
function addComponent(clave: string, found: CatalogSearchHit): void {
  const linea = items.value.find((item) => item.key === clave)
  if (linea === undefined) return

  // El precio del componente es el **de catálogo**, no una fracción del set: los
  // componentes no suman al total, así que su precio es información del desglose y no
  // una parte de lo que se va a cobrar. Aun así se enseña y se puede cambiar.
  // Al principio también, por lo mismo que las líneas: el set puede estar abajo en una
  // cotización larga, y su buscador de componentes está en su fila.
  const hijos = [...(linea.children ?? [])]

  hijos.unshift({
    product_id: found.id,
    reference: found.reference,
    name: found.name,
    price: priceFor(found),
    quantity: 1,
    retail_price: Number.parseFloat(found.retail_price),
    wholesale_price: Number.parseFloat(found.wholesale_price),
  })

  linea.children = hijos
  componentTerm.value = { ...componentTerm.value, [clave]: '' }
  componentHits.value = { ...componentHits.value, [clave]: [] }
}

function removeComponent(clave: string, indice: number): void {
  const linea = items.value.find((item) => item.key === clave)
  if (linea === undefined) return

  linea.children = (linea.children ?? []).filter((_, i) => i !== indice)
}

/** Abre o cierra el acordeón de la línea. */
function toggleExpanded(clave: string): void {
  const estabaAbierto = expandedKeys.value.includes(clave)

  expandedKeys.value = estabaAbierto
    ? expandedKeys.value.filter((otra) => otra !== clave)
    : [...expandedKeys.value, clave]
}

/**
 * Artículo a medida, escrito a mano.
 *
 * Al principio de la lista, como los del catálogo: es el mismo buscador y el mismo
 * sitio, y lo que acaba de escribir el comercial tiene que aparecer donde está
 * mirando.
 */
function addCustomItem(): void {
  const nombre = productTerm.value.trim()
  if (productTerm.value.trim() === '') return

  const linea = nuevaLinea({ custom_name: nombre, price: 0, quantity: 1 })

  items.value = [linea, ...items.value]
  productTerm.value = ''
  productHits.value = []
  void desplazarA(linea.key)
}

function removeItem(index: number): void {
  items.value = items.value.filter((_, i) => i !== index)
}

/**
 * El filtro de categoría del buscador de sets.
 *
 * Tres opciones, y la primera se llama **«Todos»**.
 *
 * Antes la primera se llamaba «Nacional» con valor `''`, que en el servidor significa
 * «sin filtro». Es decir: el botón que decía Nacional estaba buscando también los
 * premium, y además **por defecto**, que es lo que se veía. El rótulo mentía y no había
 * forma de filtrar por nacional sin resortear.
 *
 * Lo que se quería era que el filtro enseñase siempre qué está puesto, y eso se consigue
 * con un rótulo que diga la verdad —«Todos»— y no con un rótulo que prometa un filtro que
 * no se está aplicando.
 */
const setTypeOptions: Array<{ label: string; value: SetType | '' }> = [
  { label: 'Todos', value: '' },
  { label: 'Nacional', value: 'nacional' },
  { label: 'Premium', value: 'premium' },
]

/** Buscador de producto individual. */
async function searchProducts(): Promise<void> {
  const term = productTerm.value.trim()
  if (term.length < MIN_TERM_LENGTH) {
    productHits.value = []
    return
  }

  productSearching.value = true
  try {
    const { data } = await catalogApi.search({ q: term, kind: 'product' })
    productHits.value = data.data
  } catch {
    productHits.value = []
  } finally {
    productSearching.value = false
  }
}

/** Buscador de set predefinido, con el filtro Nacional / Premium. */
async function searchSets(): Promise<void> {
  const term = setTerm.value.trim()
  if (term.length < MIN_TERM_LENGTH) {
    setHits.value = []
    return
  }

  setSearching.value = true
  try {
    const { data } = await catalogApi.search({
      q: term,
      kind: 'set',
      type: setType.value === '' ? undefined : setType.value,
    })
    setHits.value = data.data
  } catch {
    setHits.value = []
  } finally {
    setSearching.value = false
  }
}

/** Cambiar el filtro de categoría vuelve a buscar: lo que hay en pantalla ya no aplica. */
function changeSetType(): void {
  void searchSets()
}

/** Buscador de componentes, dentro del acordeón de un set. */
async function searchComponents(clave: string): Promise<void> {
  const term = (componentTerm.value[clave] ?? '').trim()
  if (term.length < MIN_TERM_LENGTH) {
    componentHits.value = { ...componentHits.value, [clave]: [] }
    return
  }

  componentSearching.value = { ...componentSearching.value, [clave]: true }
  try {
    // Siempre `kind: 'product'`: lo que compone un set son piezas, no sets. Meter un
    // set dentro de un set no tiene desglose posible.
    const { data } = await catalogApi.search({ q: term, kind: 'product' })
    componentHits.value = { ...componentHits.value, [clave]: data.data }
  } catch {
    componentHits.value = { ...componentHits.value, [clave]: [] }
  } finally {
    componentSearching.value = { ...componentSearching.value, [clave]: false }
  }
}

function pickCustomer(found: CustomerSuggestion): void {
  customerId.value = found.id
  customerName.value = found.name
  customerDocument.value = found.document ?? ''
  customerEmail.value = found.email ?? ''
  customerPhone.value = found.phone ?? ''
  // Ciudad y origen también se rellenan. El comercial está con el cliente al lado y al
  // elegirlo de la lista espera verlos, y no tenerlos en un desplegable en blanco obliga a
  // elegirlos otra vez.
  customerCity.value = found.city ?? ''
  customerSource.value = found.source ?? ''
  customerSuggestions.value = []
}

async function searchCustomers(): Promise<void> {
  const term = customerName.value.trim()
  if (term.length < 2) {
    customerSuggestions.value = []
    return
  }

  try {
    const { data } = await customerApi.search(term)
    customerSuggestions.value = data.data
  } catch {
    customerSuggestions.value = []
  }
}

function extractServerErrors(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as {
    validationErrors?: Record<string, string[]>
    response?: { data?: { error?: { details?: { fields?: Record<string, string[]> } } } }
  }
  return maybeAxios.validationErrors ?? maybeAxios.response?.data?.error?.details?.fields ?? {}
}

/**
 * Sets que se han añadido y todavía no tienen componentes.
 *
 * El servidor los rechaza («Un set necesita al menos un componente en su desglose»),
 * pero un 422 sobre un campo que **no suma** es un mensaje que no ayuda: el comercial no
 * sabe que tiene que abrir un set que ve completo. Por eso el botón se bloquea aquí y
 * se dice cuál, y el servidor se queda de red de seguridad.
 */
const setsSinComponentes = computed(() =>
  items.value
    .filter((item) => item.set_id !== null && item.set_id !== undefined)
    .filter((item) => (item.children ?? []).length === 0)
    .map((item) => item.name ?? 'Set sin nombre'),
)

const canSave = computed(
  () =>
    customerName.value.trim() !== '' &&
    items.value.length > 0 &&
    setsSinComponentes.value.length === 0,
)

/**
 * ¿Guardar cambia una factura ya emitida?
 *
 * Se edita igual, pero avisando. Una factura emitida no se deshace y su número apunta
 * a lo que se le cobró: si después el importe de la cotización es otro, alguien tiene
 * que saber que las dos cosas ya no coinciden. Por eso el aviso dice cuál es la
 * factura y cuál es el importe nuevo, y no un «¿seguro?» a secas.
 *
 * Cancelar el aviso no lo recuerda para no volver a preguntar: preguntar otra vez es lo
 * correcto, porque quien cancela es que ha decidido que no. Guardar sí lleva a otra
 * pantalla, así que no hay un segundo guardado en el que volver a preguntar.
 *
 * Y es justo lo que hace que el autoguardado se pare aquí: sin el aviso, un cambio
 * reescribiría por detrás el documento que ya se facturó.
 */
function cambiaUnaFacturaEmitida(): boolean {
  return estaFacturada.value
}

const avisoFactura = ref(false)

/** El importe que se está guardando, para poderlo decir en el aviso. */
function importeActual(): string {
  return formatCurrency(total.value)
}

async function save(): Promise<void> {
  if (!canSave.value) return

  // El guardado real ocurre en `guardarDeVerdad`, al que llegan tanto el botón como el
  // diálogo. Un solo camino, para que no haya dos que se puedan ir de la sincronía.
  if (cambiaUnaFacturaEmitida()) {
    avisoFactura.value = true
    return
  }

  await guardarDeVerdad()
}

async function confirmarFactura(): Promise<void> {
  avisoFactura.value = false

  await guardarDeVerdad()
}

async function guardarDeVerdad(): Promise<void> {
  saving.value = true
  serverErrors.value = {}
  generalError.value = null

  // Se cancela el autoguardado que estuviera esperando. Si no, el guardado explícito
  // y el automático irían los dos a la vez sobre la misma cotización, y el que llegara
  // el segundo escribiría por detrás del otro.
  if (autoSaveTimer !== null) {
    clearTimeout(autoSaveTimer)
    autoSaveTimer = null
  }

  try {
    // El payload es el mismo que el del autoguardado, y no una copia: si los dos
    // tuvieran su propia lista de campos, el autoguardado acabaría guardando menos
    // que el guardado, y lo que se ve al guardar no sería lo que hay en pantalla
    // hasta hacía un segundo.
    const payload = payloadActual()

    // En el primer guardado se usa `store`; después, `update` sobre el mismo
    // borrador. `update` reemplaza los ítems, así que lo que se ve en pantalla
    // es exactamente lo que queda guardado.
    //
    // No se manda `status`. El estado no lo elige quien escribe: guardar cierra el
    // borrador —el backend pone `pendiente` en `store` y también al `update` de una
    // que estaba en `borrador`— y el resto de los cambios se hacen desde el
    // desplegable del listado.
    //
    // Nota sobre por qué `store` casi nunca es el camino: el autoguardado ya creó la
    // cotización al entrar, así que `quoteId` deja de ser `null` en el primer segundo.
    // Guardar es casi siempre un `update` sobre un borrador que ya existe, y por eso
    // el backend tiene que promover también en esa ruta: si solo lo hiciera en
    // `store`, guardarla no cambiaría nada.
    const response =
      quoteId.value === null
        ? await quoteApi.save(payload)
        : await quoteApi.update(quoteId.value, payload)

    quoteId.value = response.data.data.id
    // Lo que hay en pantalla ya está guardado, así que al salir no se manda otra vez lo
    // mismo por el autoguardado de salida.
    ultimaFirmaGuardada = firmaActual()

    // Después de guardar (crear o editar), volver al listado con los filtros preservados.
    volverAlListado()
  } catch (cause: unknown) {
    serverErrors.value = extractServerErrors(cause)
    generalError.value = Object.keys(serverErrors.value).length > 0 ? null : 'No se pudo guardar la cotización. Inténtalo de nuevo.'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  if (!allowed.value) {
    loading.value = false
    return
  }

  // Se saca a una constante porque `editId` es `number | null` por diseño —una ruta
  // sin id no es una edición— y dentro del `if` TypeScript no lo estrecha a través del
  // `computed`.
  const editando = editId.value

  try {
    if (editando !== null) {
      // Editar **no** pide un borrador nuevo. Se abre la que hay: crear otra sería
      // dejar un `borrador` vacío en el listado por el mero hecho de entrar a corregir
      // una cotización.
      const { data } = await quoteApi.get(editando)

      quoteId.value = data.data.id
      editada.value = data.data
      rellenarCon(data.data)
    } else {
      const { data } = await quoteApi.createDraft()
      quoteId.value = data.data.id

      // El asesor por defecto es el usuario autenticado, no el primero de la lista. Se
      // escribe el id que vino con el token y no el que traiga el desplegable, porque
      // el desplegable aún no está poblado y su valor por defecto sería el primer
      // asesor de la lista: atribuir la cotización a otro sin querer.
      advisorId.value = authStore.user?.id ?? null
    }
  } catch {
    generalError.value = isEdit.value
      ? 'No se pudo abrir la cotización. Revisa tu conexión e inténtalo de nuevo.'
      : 'No se pudo abrir una cotización nueva. Revisa tu conexión e inténtalo de nuevo.'
  } finally {
    loading.value = false
  }

  // Lo que hay en pantalla ahora mismo ya está en el servidor: la cotización se acaba de
  // abrir, o el borrador se acaba de crear. Se anota su firma para que el relleno del
  // formulario no cuente como un cambio —si contara, entrar a editar sin escribir
  // mandaría un autoguardado por cada campo— y para que el autoguardado de salida, si
  // alguien entra y se va sin tocar nada, no escriba lo mismo otra vez.
  ultimaFirmaGuardada = firmaActual()

  // Los asesores van aparte del borrador y su fallo no lo tumba. Sin la lista el
  // desplegable se quedaría vacío y no se podría cambiar de autor, pero el resto del
  // cotizador funciona igual: se avisa y se sigue.
  try {
    const { data } = await quoteApi.advisors()
    // El `catch` solo cubre los fallos de red: si la respuesta llega bien formada
    // pero sin lista —un sobre de error con código 200, una ruta que devuelve el
    // objeto suelto— la asignación deja `advisors` sin array y el computed de las
    // opciones revienta con «map is not a function» al pintarse. Con la lista
    // vacía el desplegable aparece sin opciones y el cotizador sigue funcionando.
    advisors.value = Array.isArray(data.data) ? data.data : []
  } catch {
    advisors.value = []
  }

  // Cargar líneas de atención disponibles (para el desplegable en edición)
  await loadAttentionLines()

  // En creación, pre-rellenar la línea de atención con la del usuario actual
  if (!editando) {
    customerAttentionLine.value = authStore.user?.attention_line ?? ''
  }
})
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          {{ titulo }}
        </h1>
        <!--
          Se dice en los dos modos, porque ahora es verdad en los dos: el autoguardado
          no distingue entre el borrador y la corrección. Antes la frase solo salía en
          el alta, y decirla al editar habría sido mentir —y callarse tampoco vale,
          porque quien corrige una cotización desde el móvil necesita saber que no la
          pierde.

          La segunda frase es solo del alta: al guardar, una cotización en borrador pasa
          a `pendiente`. En una edición guardar no cambia el estado, así que ahí no
          habría dicho verdad.
        -->
        <!--
          Solo se avisa cuando la cotización ya está facturada: ahí los cambios no se
          guardan solos, y sin esta nota el comercial creería que sí. En un borrador
          no hace falta decirlo: guardar es guardar, y el total lo calcula el servidor.
        -->
        <p
          v-if="!seGuardaSolo"
          class="mt-1 text-surface-700 dark:text-surface-300"
          data-testid="no-se-guarda-solo"
        >
          Esta cotización lleva factura emitida, así que los cambios no se guardan
          solos: hay que pulsar «Guardar cambios».
        </p>
      </div>

      <Button
        label="Volver"
        icon="pi pi-arrow-left"
        severity="secondary"
        :disabled="saving"
        @click="volverALista()"
      />
    </header>

    <Message
      v-if="!allowed"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      No tienes permiso de gestión de cotizaciones, así que no puedes crear ninguna.
    </Message>

    <Message
      v-if="autoSaveError"
      severity="warn"
      :closable="false"
      variant="simple"
      role="status"
    >
      {{ autoSaveError }}
    </Message>

    <Message
      v-if="generalError"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ generalError }}
    </Message>

    <Message
      v-if="Object.keys(serverErrors).length > 0"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      Revisa los campos marcados: {{ Object.values(serverErrors).flat().join(' ') }}
    </Message>

    <template v-if="allowed">
      <!--
        Los datos de la cabecera, sin tarjeta con título.

        Se tituló «Cabecera» y no hacía falta nada: dentro solo hay campos con su
        etiqueta —Asesor, Tipo de facturación, Cliente, Documento…— que ya dicen qué son.
        Un rótulo que no aporta nada ocupa una fila de alto y hay que saltárselo con la
        vista en cada entrada.

        La tarjeta sigue, porque el borde y el fondo sí separan esta parte de los
        artículos.
      -->
      <Accordion
        v-model:value="expandedTabs"
        :multiple="true"
        class="w-full"
      >
        <!--
          El `headerStyleClass` y el `contentStyleClass` de la estructura antigua
          pasan a clase directa en `AccordionHeader` y `AccordionContent`, que es
          donde vive ahora cada parte.
        -->
        <AccordionPanel
          value="datos"
          :pt="{ root: { class: 'bg-surface-100 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700 shadow-sm' } }"
        >
          <AccordionHeader
            :pt="{ root: { class: 'flex items-center justify-between cursor-pointer hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors' } }"
          >
            <div class="flex items-center justify-between w-full py-2">
              <div class="flex items-center gap-2">
                <i class="pi pi-id-card text-primary-600 dark:text-primary-400" />
                <span class="font-medium text-surface-900 dark:text-surface-100">
                  Datos de la cotización
                </span>
              </div>
              <i class="pi pi-chevron-down text-surface-500 dark:text-surface-400 transition-transform duration-200 text-lg" />
            </div>
          </AccordionHeader>

          <AccordionContent
            :pt="{ root: { class: 'p-4 gap-4' } }"
          >
            <!--
            El orden va de la identidad de la cotización a la del cliente porque es el
            que se lee: primero quién la hace y a qué precio, después a quién se le
            manda. Los artículos van en otra tarjeta y más abajo, porque son la parte
            larga y bajarlos deja la cabecera a la vista mientras se teclea.

            El asterisco va solo en el cliente: es lo único que sin él no se puede
            guardar. Correo y teléfono son opcionales, y un asterisco sobre un campo
            opcional obliga a inventar el dato para poder continuar.
          -->
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div class="space-y-2">
                <label
                  for="quote-advisor"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Asesor
                </label>
                <Select
                  id="quote-advisor"
                  v-model="advisorId"
                  :options="advisorOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Asesor"
                  class="w-full"
                  :invalid="!!serverErrors.user_id"
                />
              </div>

              <div class="space-y-2">
                <label
                  for="quote-price-type"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Tipo de facturación
                </label>
                <Select
                  id="quote-price-type"
                  v-model="priceType"
                  :options="priceTypeOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Tipo de facturación"
                  class="w-full"
                  :invalid="!!serverErrors.price_type"
                />
              </div>

              <div class="space-y-2">
                <label
                  for="quote-customer"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Cliente <span class="text-red-500">*</span>
                </label>
                <InputText
                  id="quote-customer"
                  v-model="customerName"
                  placeholder="Nombre o documento"
                  class="w-full"
                  :invalid="!!serverErrors.customer_name"
                  @input="searchCustomers"
                />
                <p class="text-xs text-surface-500">
                  Escribe el nombre o el número de documento. Da igual con puntos o sin ellos: el cliente sale igual.
                </p>
                <ul
                  v-if="customerSuggestions.length > 0"
                  class="divide-y divide-surface-200 rounded border border-surface-200 dark:divide-surface-700 dark:border-surface-700"
                >
                  <li
                    v-for="option in customerSuggestions"
                    :key="option.id"
                  >
                    <button
                      type="button"
                      class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-surface-100 dark:hover:bg-surface-800"
                      @click="pickCustomer(option)"
                    >
                      <span>{{ option.name }}</span>
                      <span
                        v-if="option.document"
                        class="font-mono text-xs text-surface-500"
                      >
                        {{ option.document }}
                      </span>
                    </button>
                  </li>
                </ul>
              </div>

              <div class="space-y-2">
                <label
                  for="quote-document"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Documento
                </label>
                <InputText
                  id="quote-document"
                  v-model="customerDocument"
                  placeholder="900123456-1"
                  class="w-full"
                  :invalid="!!serverErrors.document"
                />
              </div>

              <div class="space-y-2">
                <label
                  for="quote-email"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Correo electrónico
                </label>
                <InputText
                  id="quote-email"
                  v-model="customerEmail"
                  type="email"
                  inputmode="email"
                  autocomplete="email"
                  placeholder="compras@empresa.co"
                  class="w-full"
                  :invalid="!!serverErrors.customer_email"
                />
              </div>

              <div class="space-y-2">
                <label
                  for="quote-phone"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Teléfono
                </label>
                <InputText
                  id="quote-phone"
                  v-model="customerPhone"
                  type="tel"
                  inputmode="tel"
                  autocomplete="tel"
                  placeholder="3001234567"
                  class="w-full"
                  :invalid="!!serverErrors.customer_phone"
                />
              </div>

              <!--
              Ciudad y origen. No es relleno: el cotizador crea el cliente en el
              directorio, y sin estos dos campos el cliente nacía a medias. El origen sobre
              todo, porque es por dónde se segmenta la cartera —cuántas cotizaciones vienen
              de la web, cuántas de una visita— y un origen sin rellenar hace que esas cifras
              midan menos de lo que parecen medir, y en silencio.
            -->
              <div class="space-y-2">
                <label
                  for="quote-city"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Ciudad
                </label>
                <InputText
                  id="quote-city"
                  v-model="customerCity"
                  autocomplete="address-level2"
                  placeholder="Bogotá"
                  class="w-full"
                  :invalid="!!serverErrors.customer_city"
                />
              </div>

              <div class="space-y-2">
                <label
                  for="quote-source"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Origen
                </label>
                <Select
                  id="quote-source"
                  v-model="customerSource"
                  :options="customerSourceOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Sin origen"
                  aria-label="Origen del cliente"
                  class="w-full"
                  :invalid="!!serverErrors.customer_source"
                />
              </div>

              <!--
              Línea de atención.
              - En CREACIÓN: se pre-rellena con la del usuario autenticado y es NO EDITABLE.
              - En EDICIÓN: es un desplegable con la línea que ya tiene la cotización
                más las que tienen los usuarios, sin repetir, y con «Sin línea» para
                poder quitársela.
            -->
              <div class="space-y-2">
                <label
                  for="quote-attention-line"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Línea de atención
                </label>
                <InputText
                  v-if="!isEdit"
                  id="quote-attention-line"
                  v-model="customerAttentionLine"
                  disabled
                  class="w-full"
                  :invalid="!!serverErrors.attention_line"
                />
                <!--
                Select nativo estilizado como PrimeVue.
                
                El componente Select de PrimeVue 4 tiene un bug con opciones dinámicas:
                no renderiza las opciones aunque los datos sean correctos y reactivos.
                El select nativo funciona perfectamente y lo estilizamos para que parezca
                PrimeVue. Cuando se arregle el bug en PrimeVue, se puede volver a cambiar.
              -->
                <select
                  v-else
                  id="quote-attention-line"
                  v-model="customerAttentionLine"
                  class="w-full appearance-none bg-white border border-surface-300 rounded-lg py-2 pl-3 pr-10 text-sm text-surface-900 dark:bg-surface-900 dark:border-surface-600 dark:text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  :class="{
                    'border-red-500': !!serverErrors.attention_line,
                    'dark:border-red-500': !!serverErrors.attention_line,
                  }"
                >
                  <option value="">
                    Seleccione una línea
                  </option>
                  <option
                    v-for="opt in attentionLineSelectOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>

                <!-- Flecha del dropdown estilo PrimeVue -->
                <div class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    class="text-surface-400 dark:text-surface-500"
                  >
                    <path
                      d="M3.5 5.25L7 8.75L10.5 5.25"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>

        <!--
          Anticipos, y solo al editar.

          En una cotización nueva no hay nada que anticipar: no se ha cobrado nada y el
          cliente aún no ha visto el precio. Al editar sí, y entonces tener que abrir la
          ficha para anotar un pago es un paso de más en mitad de la corrección.

          Se deja la segunda pestaña cerrada. El acordeón abre la primera —los datos de
          la cotización— porque es lo que se corrige casi siempre, y abrir esto de
          entrada taparía justo eso. Lo que sí se ve sin abrir nada es el nombre de la
          pestaña, que es lo que hace falta para saber que está.
        -->
        <AccordionPanel
          v-if="isEdit"
          value="anticipos"
          :pt="{ root: { class: 'bg-surface-100 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700 shadow-sm' } }"
        >
          <AccordionHeader
            :pt="{ root: { class: 'flex items-center justify-between cursor-pointer hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors' } }"
          >
            <div class="flex items-center justify-between w-full py-2">
              <div class="flex items-center gap-2">
                <i class="pi pi-wallet text-primary-600 dark:text-primary-400" />
                <span class="font-medium text-surface-900 dark:text-surface-100">
                  Anticipos
                </span>
              </div>
              <i class="pi pi-chevron-down text-surface-500 dark:text-surface-400 transition-transform duration-200 text-lg" />
            </div>
          </AccordionHeader>

          <AccordionContent
            :pt="{ root: { class: 'p-4 gap-4' } }"
          >
            <!--
            El `v-if` no es una precaution: sin él la tarjeta pediría anticipos sobre la
            cotización `0` mientras se está abriendo, y esa llamada no existe.
          -->
            <AdvancesCard
              v-if="quoteId !== null"
              anidada
              :quote-id="quoteId"
              :advances="advances"
              :advances-total="advancesTotal"
              :balance="balance"
              @cambia="cambiaAnticipos"
            />
          </AccordionContent>
        </AccordionPanel>
      </Accordion>

      <!-- Ítems -->
      <AppCard>
        <template #header>
          <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
            Artículos
          </h2>
        </template>

        <!--
          `space-y-1` y no `space-y-4`: una cotización larga son decenas de filas, y el
          aire entre ellas se acumula mucho más que el aire dentro.
        -->
        <div class="space-y-1">
          <!--
            Los dos buscadores, en paralelo y no en pestañas.

            Un pedido lleva piezas sueltas y conjuntos a la vez, así que alternar entre
            dos modos para cada línea era un paso por línea que no compraba nada. Cada
            uno tiene su término, sus resultados y su botón de «a medida».
          -->
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <!--
              Tarjeta **anidada** dentro de la de artículos, y con el tema eso son sesenta
              píxeles de relleno vertical entre el título y el campo: `px-5 pt-4 pb-3` de
              la cabecera más `px-5 py-4` del cuerpo. La variante apretada deja treinta y
              dos, y el título pegado al campo.
            -->
            <AppCard class="card-apretada">
              <template #header>
                <h3 class="flex items-center gap-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
                  <i
                    class="pi pi-box text-primary-600"
                    aria-hidden="true"
                  />
                  Buscar Producto Individual
                </h3>
              </template>

              <div class="space-y-2">
                <div class="flex flex-col gap-2 sm:flex-row">
                  <label
                    for="quote-product-search"
                    class="sr-only"
                  >
                    Buscar un producto por código o nombre
                  </label>
                  <InputText
                    id="quote-product-search"
                    v-model="productTerm"
                    placeholder="Código o nombre..."
                    class="w-full"
                    @input="searchProducts"
                  />
                  <!--
                    El botón de «a medida» sale **solo cuando no hay nada que añadir del
                    catálogo**.

                    Con resultados a la vista es un botón más que no hace falta: el
                    artículo que buscas está en la lista de sugerencias y basta con
                    pulsarlo. Y ocupaba sitio al lado del campo en cada línea de búsqueda.

                    Aparece cuando se ha escrito algo y **no** sale nada, que es el
                    único momento en que sirve: cuando lo que se busca no está en el
                    catálogo y la línea hay que escribirla a mano. Sin esto no habría forma
                    de cotizar algo que el catálogo no tiene.
                  -->
                  <Button
                    v-if="productTerm.trim() !== '' && productHits.length === 0"
                    :label="`Añadir «${productTerm.trim()}»`"
                    icon="pi pi-plus"
                    severity="secondary"
                    outlined
                    data-testid="quote-custom-item"
                    @click="addCustomItem"
                  />
                </div>

                <!--
                  Las sugerencias **envuelven** en vez de ir una por fila.

                  Diez resultados en columna son unos trescientos píxeles de alto, y en
                  una cotización larga ese hueco se repite cada vez que se busca. En
                  líneas que envuelven ocupan la mitad, y caben más de un resultado por
                  fila en cuanto el nombre de la pantalla da de sí.

                  `flex-wrap` y no `grid`: el ancho de cada botón depende de lo largo que
                  sea el nombre, y una rejilla los deja todos iguales con huecos.
                -->
                <ul
                  v-if="productHits.length > 0"
                  data-testid="product-suggestions"
                  class="flex flex-wrap gap-1"
                >
                  <li
                    v-for="option in productHits"
                    :key="option.id"
                  >
                    <button
                      type="button"
                      class="flex max-w-full items-start gap-2 rounded-lg border border-surface-200 px-2 py-1 text-left text-sm transition-colors hover:border-primary-500 hover:bg-surface-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-surface-700 dark:hover:bg-surface-800"
                      :title="`${option.reference} — ${option.name}`"
                      @click="addProduct(option)"
                    >
                      <!--
                        En movil el nombre se parte en varias lineas: nombres como
                        `ESTAMPADO, MODIFICACIONES LOGOS, LOGO, DOMICILIO` son mas
                        anchos que un movil, y con recorte no hay forma de saber
                        cual se esta añadiendo sin abrirlo. En escritorio se
                        recorta con `...`, que es lo que ya hacia, y el `title`
                        sigue teniendo el nombre entero.
                      -->
                      <span class="min-w-0 flex-1 break-words sm:truncate sm:whitespace-nowrap">{{ option.reference }} — {{ option.name }}</span>
                      <span class="shrink-0 font-medium tabular-nums pt-0.5">{{ formatCurrency(priceFor(option)) }}</span>
                    </button>
                  </li>
                </ul>

                <p
                  v-else-if="productTerm.trim().length >= 2 && !productSearching"
                  class="text-sm text-surface-500 dark:text-surface-400"
                >
                  Ningún producto coincide.
                </p>
              </div>
            </AppCard>

            <AppCard class="card-apretada">
              <template #header>
                <h3 class="flex items-center gap-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
                  <i
                    class="pi pi-clone text-primary-600"
                    aria-hidden="true"
                  />
                  Buscar Set Predefinido
                </h3>
              </template>

              <!--
                El filtro va **antes** del campo, y pegado a él. `space-y-2` en vez del
                general: el aire entre el rótulo, el filtro y el campo se suma tres veces
                y en un panel que solo tiene tres cosas eso es casi todo su alto.
              -->
              <div class="space-y-2">
                <!--
                  Todos / Nacional / Premium como botones y no como desplegable: son tres
                  opciones y caben sin abrir nada, que es lo que hace un desplegable en
                  móvil. El filtro lo aplica el servidor; en el cliente dejaría la lista
                  corta y el total sin cuadrar con la paginación.

                  Y cada botón manda lo que dice: «Nacional» manda `nacional`, «Premium»
                  manda `premium`, y solo «Todos» manda nada.
                -->
                <div
                  class="flex overflow-hidden rounded-lg border border-surface-300 dark:border-surface-600"
                  role="group"
                  aria-label="Categoría del set"
                >
                  <button
                    v-for="opcion in setTypeOptions"
                    :key="opcion.value"
                    type="button"
                    class="flex-1 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                    :class="setType === opcion.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-surface-700 hover:bg-surface-50 dark:bg-surface-800 dark:text-surface-200 dark:hover:bg-surface-700'"
                    :aria-pressed="setType === opcion.value"
                    @click="setType = opcion.value; changeSetType()"
                  >
                    {{ opcion.label }}
                  </button>
                </div>

                <div class="flex flex-col gap-2 sm:flex-row">
                  <label
                    for="quote-set-search"
                    class="sr-only"
                  >
                    Buscar un set por nombre
                  </label>
                  <InputText
                    id="quote-set-search"
                    v-model="setTerm"
                    placeholder="Nombre del set..."
                    class="w-full"
                    @input="searchSets"
                  />
                </div>

                <ul
                  v-if="setHits.length > 0"
                  data-testid="set-suggestions"
                  class="flex flex-wrap gap-1"
                >
                  <li
                    v-for="option in setHits"
                    :key="option.id"
                  >
                    <button
                      type="button"
                      class="flex max-w-full items-start gap-2 rounded-lg border border-surface-200 px-2 py-1 text-left text-sm transition-colors hover:border-primary-500 hover:bg-surface-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-surface-700 dark:hover:bg-surface-800"
                      :title="`${option.reference} — ${option.name}`"
                      @click="addSet(option)"
                    >
                      <!--
                        Misma regla que en el buscador de productos: en movil se
                        parte en varias lineas y en escritorio se recorta, con el
                        nombre entero en el `title`.
                      -->
                      <span class="min-w-0 flex-1 break-words sm:truncate sm:whitespace-nowrap">{{ option.reference }} — {{ option.name }}</span>
                      <span
                        class="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium"
                        :class="option.type === 'premium'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                          : 'bg-surface-200 text-surface-700 dark:bg-surface-700 dark:text-surface-200'"
                      >
                        {{ option.type === 'premium' ? 'Premium' : 'Nacional' }}
                      </span>
                    </button>
                  </li>
                </ul>

                <p
                  v-else-if="setTerm.trim().length >= 2 && !setSearching"
                  class="text-sm text-surface-500 dark:text-surface-400"
                >
                  Ningún set coincide.
                </p>
              </div>
            </AppCard>
          </div>

          <!--
            Las líneas ya añadidas.

            Un set se pinta con su acordeón: referencia, nombre, precio, cantidad, un
            botón para abrirla y otro para borrarla. Abierta, sus componentes con su
            buscador.

            El precio y la cantidad del set son los que cuentan. Los de los componentes
            son información del desglose, y está dicho en el panel de los:set y aquí
            dentro para que nadie revise una aritmética que no va a cuadrar.
          -->
          <!--
            Los sets llevan fondo tintado y una línea de pelo arriba, para separarlos del
            resto y de entre sí. Sin recuadro: un borde alrededor desplaza el contenido y
            con la rejilla eso descuadra las columnas, que es justo lo que la rejilla
            viene a arreglar.
          -->
          <div
            v-for="(item, index) in items"
            :key="item.key"
            :data-linea="item.key"
            :class="item.set_id
              ? 'bg-primary-50/70 dark:bg-primary-900/15'
              : 'border-b border-surface-200 dark:border-surface-800'"
          >
            <!--
              Una sola línea, densa.

              Antes eran dos: el nombre con su referencia debajo, y luego una fila de
              controles con sus etiquetas encima. En una cotización de treinta artículos
              eso son treinta tarjetas de unos ciento cuarenta píxeles, más de cinco
              pantallas de scroll para nada.

              Ahora el nombre se corta con puntos suspensivos y los controles van en línea
              con el nombre. Las etiquetas «$» y «Cant.» desaparecen y quedan como
              `aria-label`: se leen igual de bien mirando la pantalla y no ocupan alto.
            -->
            <!--
              Rejilla con columnas fijas, no `flex`.

              Con `flex-wrap`, un nombre largo empujaba los controles a la línea de abajo
              y cada fila quedaba a una altura distinta: los precios no alineaban entre
              filas y había que leerlos uno a uno. Con columnas fijas, **toda** fila
              tiene la misma altura y el precio, la cantidad y las acciones caen en la
              misma columna en todas.

              Responsive:
              - Móvil (<640px): 2 columnas. Fila 1: Nombre (span 2) + botones a la derecha.
                Fila 2: Precio 50% | Cantidad 50%.
              - Tablet (640-1023px): 6 columnas fijas.
              - Desktop (≥1024px): igual que tablet.
            -->
            <div
              class="relative grid gap-x-2 gap-y-2.5 px-2 py-1.5
                     grid-cols-[1fr_1fr]
                     sm:grid-cols-[1.5rem_minmax(0,1fr)_12rem_6rem_2rem_2rem]
                     md:grid-cols-[1.5rem_minmax(0,1fr)_12rem_6rem_2rem_2rem]"
            >
              <!--
                Botones de acción (expandir set, borrar) en móvil: arriba a la derecha.
                En tablet/desktop están en sus columnas fijas.
              -->
              <div
                class="col-span-2 flex justify-end gap-1 sm:hidden"
                role="group"
                aria-label="Acciones de la línea"
              >
                <Button
                  v-if="item.set_id"
                  :icon="expandedKeys.includes(item.key) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                  text
                  rounded
                  :aria-label="expandedKeys.includes(item.key) ? 'Cerrar los componentes del set' : 'Ver los componentes del set'"
                  :aria-expanded="expandedKeys.includes(item.key)"
                  :title="(item.children ?? []).length + ' componente(s)'"
                  class="!p-2 !size-10"
                  @click="toggleExpanded(item.key)"
                />
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  aria-label="Quitar artículo"
                  class="!p-2 !size-10"
                  @click="removeItem(index)"
                />
              </div>
              <!--
                El icono de la caja, que solo los sets llevan, y solo en `sm:`.
                En tablet va en la primera columna (1.5rem) centrado; en móvil oculto.
              -->
              <i
                v-if="item.set_id"
                class="pi pi-box col-start-1 row-start-1 hidden text-xl text-primary-600 sm:flex sm:items-center sm:justify-center dark:text-primary-400"
                aria-hidden="true"
              />
              <!--
                El nombre: en móvil ocupa las 2 columnas; en tablet+ en su columna.
              -->
              <div
                class="col-span-2 sm:col-start-2 min-w-0 flex items-center gap-2"
              >
                <div class="min-w-0 flex-1">
                  <!--
                    El nombre se parte en varias lineas en movil y se recorta en
                    escritorio. En un movil un articulo llamado `Collar de acero
                    quirurgico con grabado personalizado` no cabe en una linea, y
                    recortado no se distingue de otro collar que empiece igual.

                    Es lo mismo que la grilla de componentes ya hacia y que aqui
                    faltaba: el `truncate` era sin prefijo, así que también cortaba
                    en movil. `sm:truncate sm:whitespace-nowrap` deja el recorte
                    para cuando hay ancho de sobra.
                  -->
                  <span
                    class="break-words font-medium text-base text-surface-900 dark:text-surface-100 sm:whitespace-nowrap sm:truncate"
                    :title="item.name ?? item.custom_name ?? ''"
                  >{{ item.name ?? item.custom_name ?? 'Artículo' }}</span>
                  <span
                    v-if="item.reference"
                    class="ml-1.5 shrink-0 font-mono text-xs text-surface-500 dark:text-surface-400 whitespace-nowrap"
                  >({{ item.reference }})</span>
                </div>

                <!--
                  El aviso va **en línea** con el nombre, no debajo.
                -->
                <span
                  v-if="item.set_id && (item.children ?? []).length === 0"
                  class="shrink-0 text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap"
                  data-testid="set-empty-warning"
                >
                  Añade al menos un componente
                </span>
              </div>

              <!-- Precio y Cantidad: en móvil fila 2, 50/50; en tablet+ en sus columnas -->
              <div class="col-start-1 sm:col-start-3">
                <InputNumber
                  :id="`item-price-${item.key}`"
                  v-model="item.price"
                  :min="0"
                  mode="currency"
                  currency="COP"
                  locale="es-CO"
                  :min-fraction-digits="0"
                  :max-fraction-digits="2"
                  :aria-label="`Precio de ${item.name ?? 'la línea'}`"
                  class="w-full !py-1.5 text-base"
                />
              </div>

              <div class="col-start-2 sm:col-start-4">
                <InputNumber
                  :id="`item-qty-${item.key}`"
                  v-model="item.quantity"
                  :min="0.0001"
                  locale="es-CO"
                  :min-fraction-digits="1"
                  :max-fraction-digits="2"
                  show-buttons
                  :aria-label="`Cantidad de ${item.name ?? 'la línea'}`"
                  class="w-full !py-1.5 text-base"
                />
              </div>

              <!-- Botones expandir/borrar en tablet+ -->
              <div class="hidden sm:block sm:col-start-5 self-center">
                <Button
                  v-if="item.set_id"
                  :icon="expandedKeys.includes(item.key) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                  text
                  rounded
                  :aria-label="expandedKeys.includes(item.key) ? 'Cerrar los componentes del set' : 'Ver los componentes del set'"
                  :aria-expanded="expandedKeys.includes(item.key)"
                  :title="(item.children ?? []).length + ' componente(s)'"
                  class="!p-1.5"
                  @click="toggleExpanded(item.key)"
                />
              </div>
              <div class="hidden sm:block sm:col-start-6 self-center">
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  aria-label="Quitar artículo"
                  class="!p-1.5"
                  @click="removeItem(index)"
                />
              </div>
            </div>

            <!-- El acordeón del set -->
            <div
              v-if="item.set_id && expandedKeys.includes(item.key)"
              class="space-y-3 border-t border-surface-200 bg-surface-50 p-3 dark:border-surface-700 dark:bg-surface-800/50"
              :data-testid="`set-breakdown-${item.key}`"
            >
              <ul
                v-if="item.children && item.children.length > 0"
                class="space-y-2"
              >
                <!--
                  Rejilla también aquí, por lo mismo que en la línea: con `flex-wrap` los
                  componentes con nombre largo partían la fila y las cantidades no caían
                  en la misma columna. Y los desgloses largos son los que más espacio
                  consumen.
                -->
                <li
                  v-for="(hijo, indiceHijo) in item.children"
                  :key="`${item.key}-${indiceHijo}`"
                  class="grid gap-x-2 gap-y-2.5 rounded border border-surface-200 bg-white px-2 py-1.5 dark:border-surface-700 dark:bg-surface-900
                         grid-cols-[1fr]
                         sm:grid-cols-[1fr_12rem_8rem_2rem]
                         md:grid-cols-[1fr_12rem_10rem_2rem]"
                >
                  <!-- FILA 1: Nombre (siempre en su propia fila, ancho completo en móvil, col-1 en tablet+) -->
                  <span
                    class="row-start-1 col-start-1 min-w-0 text-base text-surface-900 dark:text-surface-100 pb-1.5 sm:whitespace-nowrap sm:truncate"
                    :title="hijo.name ?? hijo.custom_name ?? ''"
                  >
                    <!--
                      El nombre se parte en varias lineas en movil y se recorta en
                      escritorio, igual que la linea padre.
                    -->
                    <span class="break-words font-medium">{{ hijo.name ?? hijo.custom_name ?? 'Componente' }}</span>
                    <span
                      v-if="hijo.reference"
                      class="ml-1.5 shrink-0 font-mono text-xs text-surface-500 dark:text-surface-400 whitespace-nowrap"
                    >({{ hijo.reference }})</span>
                  </span>

                  <!-- FILA 2: PRECIO (col-2 tablet, col-1 móvil) | CANTIDAD (col-3 tablet, col-1 móvil) -->
                  <div class="row-start-2 col-start-1 sm:col-start-2 sm:row-start-2">
                    <InputNumber
                      :id="`child-price-${item.key}-${indiceHijo}`"
                      v-model="hijo.price"
                      :min="0"
                      mode="currency"
                      currency="COP"
                      locale="es-CO"
                      :min-fraction-digits="0"
                      :max-fraction-digits="2"
                      :aria-label="`Precio de ${hijo.name ?? 'el componente'}`"
                      class="w-full !py-1.5 text-base"
                    />
                  </div>

                  <div class="row-start-2 col-start-1 sm:col-start-3 sm:row-start-2">
                    <InputNumber
                      :id="`child-qty-${item.key}-${indiceHijo}`"
                      v-model="hijo.quantity"
                      :min="0.0001"
                      locale="es-CO"
                      :min-fraction-digits="1"
                      :max-fraction-digits="2"
                      show-buttons
                      :aria-label="`Cantidad de ${hijo.name ?? 'el componente'}`"
                      class="w-full !py-1.5 text-base"
                    />
                  </div>

                  <!-- Botón borrar: en móvil fila 1 a la derecha; en tablet fila 1 col-4 -->
                  <Button
                    icon="pi pi-times"
                    severity="danger"
                    text
                    rounded
                    :aria-label="`Quitar ${hijo.name ?? 'el componente'}`"
                    class="row-start-1 col-start-1 justify-self-end !p-1.5 sm:col-start-4 sm:row-start-1 sm:justify-self-start md:col-start-4"
                    @click="removeComponent(item.key, indiceHijo)"
                  />
                </li>
              </ul>

              <div class="flex flex-col gap-2 sm:flex-row">
                <label
                  :for="`component-search-${item.key}`"
                  class="sr-only"
                >
                  Buscar un producto para añadirlo al set {{ item.name }}
                </label>
                <InputText
                  :id="`component-search-${item.key}`"
                  :value="componentTerm[item.key] ?? ''"
                  :placeholder="`Añadir un producto a ${item.name ?? 'el set'}…`"
                  class="w-full"
                  @update:model-value="componentTerm = { ...componentTerm, [item.key]: $event ?? '' }"
                  @input="searchComponents(item.key)"
                />
              </div>

              <!--
                Con la clave del set en el `data-testid`, porque puede haber varios
                `<ul>` de sugerencias a la vez —uno por set abierto— y sin ella no hay
                forma de saber cuál se está mirando.
              -->
              <ul
                v-if="(componentHits[item.key] ?? []).length > 0"
                :data-testid="`component-suggestions-${item.key}`"
                class="divide-y divide-surface-200 rounded border border-surface-200 bg-white dark:divide-surface-700 dark:border-surface-700 dark:bg-surface-900"
              >
                <li
                  v-for="option in componentHits[item.key] ?? []"
                  :key="option.id"
                >
                  <button
                    type="button"
                    class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-surface-100 dark:hover:bg-surface-800"
                    @click="addComponent(item.key, option)"
                  >
                    <span class="min-w-0 flex-1 break-words sm:truncate sm:whitespace-nowrap">
                      {{ option.reference }} — {{ option.name }}
                    </span>
                    <span class="shrink-0 font-medium">{{ formatCurrency(priceFor(option)) }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <p
            v-if="items.length === 0"
            class="py-8 text-center text-sm text-surface-500 dark:text-surface-400"
          >
            Todavía no hay artículos. Busca un producto o un set arriba.
          </p>
        </div>
      </AppCard>

      <!--
        La barra de abajo no se va nunca.

        En una cotización larga el botón de guardar queda a tres pantallas de scroll del
        precio, y el total estaba dentro de la tarjeta de artículos, también a tres
        pantallas. Así que para comprobar cuánto va a salir hay que subir, y para guardar
        hay que bajar. Con la barra fija, las dos cosas están siempre a la vista y en el
        mismo sitio: el total se mira y se guarda sin moverse.

        `sticky bottom-0` y no `fixed`: `sticky` ocupa su sitio en el flujo, así que no
        hay que reserving altura a mano ni tapar el último artículo con el botón. El
        cierre está en el hueco del fondo, para que la fila de arriba siempre esté
        pegada al borde.
      -->
      <div class="sticky bottom-0 z-10 -mx-4 border-t border-surface-200 bg-surface-50/95 px-4 pb-4 pt-3 backdrop-blur dark:border-surface-700 dark:bg-surface-950/95 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 pb-safe">
        <!--
          El total ARRIBA, los botones ABAJO en móvil.
          En desktop: total a la izquierda, botones a la derecha.
        -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <!-- TOTAL - arriba en móvil, izquierda en desktop -->
          <div class="self-start sm:self-auto">
            <p class="text-xs uppercase tracking-wide text-surface-500 dark:text-surface-400">
              Total
            </p>
            <p
              data-testid="quote-total"
              class="text-2xl font-semibold text-surface-900 dark:text-surface-100"
              aria-live="polite"
            >
              {{ formatCurrency(total) }}
            </p>
          </div>

          <!-- BOTONES - abajo en móvil, derecha en desktop -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <!--
              Al editar se llama «Volver» y no «Cancelar» porque ya no se cancela nada:
              lo escrito se ha guardado solo. Un botón que se llama «Cancelar» y no
              cancela es peor que un botón mal colocado —el comercial lo pulsa creyendo
              que ha deshecho algo y lo que ha hecho es cerrar la pantalla—.
            -->
            <Button
              :label="isEdit ? 'Volver' : 'Descartar'"
              severity="secondary"
              :disabled="saving"
              @click="volverALista()"
            />
            <Button
              :label="isEdit ? 'Guardar cambios' : 'Guardar cotización'"
              icon="pi pi-check"
              :loading="saving"
              :disabled="!canSave"
              data-testid="quote-save"
              @click="save"
            />

            <!--
          El autoguardado se anuncia, aunque no avise. Es la diferencia entre «mi
          trabajo está a salvo» y «a ver si se guarda», y sin decir nada el usuario no
          tiene forma de saber si lo que escribió se conserva.
        -->
            <p
              v-if="!saving && seGuardaSolo"
              class="self-center text-xs sm:self-end"
              :class="autoSaving ? 'text-surface-500 dark:text-surface-400' : 'text-surface-400 dark:text-surface-500'"
              aria-live="polite"
              data-testid="auto-save-status"
            >
              <!--
            Dos iconos en vez de uno con la clase cambiada por expresión: `pi-spin` tiene
            que ir junto a su icono en el mismo atributo estático, porque `pi-spin` a
            secas no pinta nada y el spinner sería un hueco vacío. Hay un test que lo
            comprueba sobre el código, no sobre la pantalla.
          -->
              <i
                v-if="autoSaving"
                class="pi pi-spinner pi-spin mr-1 text-xs"
                aria-hidden="true"
              />
              <i
                v-else
                class="pi pi-cloud mr-1 text-xs"
                aria-hidden="true"
              />
              {{ autoSaving ? 'Guardando…' : 'Se guarda solo' }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <!--
      Guardar sobre una factura emitida.

      Se edita igual, pero avisando: el número de factura apunta a lo que se le cobró y
      el importe nuevo es otro. El aviso dice las dos cosas —qué factura y qué importe—
      porque un «¿seguro?» sin más no sirve para decidir.

      Cancelar el aviso y volver a pulsar «Guardar» lo vuelve a enseñar: quien cancela es
      que ha decidido que no, y guardar sin más ya sería saltárselo.
    -->
    <ConfirmDialog
      v-model:visible="avisoFactura"
      title="Esta cotización ya está facturada"
      confirm-label="Guardar igualmente"
      @confirm="confirmarFactura"
      @cancel="avisoFactura = false"
    >
      <div
        v-if="editada"
        class="space-y-2"
      >
        <p class="text-surface-700 dark:text-surface-300">
          La factura
          <strong>{{ editada.invoice_number ?? '—' }}</strong>
          ya está emitida. Al guardar, el importe de la cotización pasa a
          <strong>{{ importeActual() }}</strong> y esa factura ya no coincide con lo que
          dice la cotización.
        </p>
        <p class="text-sm text-surface-500 dark:text-surface-400">
          Si lo que cambia es un error de la factura, lo correcto es emitir una nueva, no
          cambiar la cotización por detrás de la que ya salió.
        </p>
      </div>
    </ConfirmDialog>
  </div>
</template>
